import classNames from "classnames";
import Shape from "ui/atoms/Shape/Shape";
import times from "util/times";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { SpeakerWaveIcon } from "@heroicons/react/24/solid";

interface Props {
  answer?: "a" | "b" | "neither";
  questionText: string;
  answerText?: string;
  onClick: (answer: "a" | "b" | "neither") => void;
  playbackState?: "a" | "b";
}

export default function ABPicker({
  answer,
  questionText,
  answerText,
  onClick,
  playbackState,
}: Props) {
  const [hasBeenClicked, setHasBeenClicked] = useState<Props["answer"] | null>(
    null
  );
  const handleClick = (answer: "a" | "b" | "neither") => () => {
    if (!hasBeenClicked) {
      setHasBeenClicked(answer);
    }
  };

  const handleOnAnimateComplete = () => {
    if (hasBeenClicked) {
      onClick(hasBeenClicked);
    }
  };

  return (
    <motion.div
      onAnimationComplete={handleOnAnimateComplete}
      animate={!hasBeenClicked && !answer ? "open" : "closed"}
      className={classNames("relative z-0 mb-28 inline-flex md:mb-0", {
        "pointer-events-none": answer || hasBeenClicked,
      })}
    >
      <div
        className={classNames(
          "relative mr-2 w-44 md:mr-32 md:w-52",
          answer === "a" ? "z-20" : "z-10"
        )}
      >
        {answer && answer === "a" && (
          <div
            className={
              "absolute -inset-8 animate-pulse rounded-full bg-blue-400/50"
            }
          />
        )}

        <Shape
          disabled={answer && answer !== "a"}
          type="content"
          theme={"blue"}
          shape={"circle"}
          onClick={handleClick("a")}
          active={hasBeenClicked === "a"}
        >
          <div className={"relative mb-4"}>
            <AnimatePresence>
              {playbackState === "a" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <SpeakerWaveIcon
                    className={
                      "absolute -top-5 left-1/2 h-5 w-5 -translate-x-1/2 animate-pulse"
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className={"font-brik text-6xl leading-none md:text-8xl"}>
              A
            </div>
            <div className={"font-brik text-xs leading-none"}>
              {answer && answer === "a" && answerText}
              {!answer && questionText}
            </div>
          </div>
        </Shape>

        {!answer &&
          times(12, (i) => (
            <motion.div
              key={i}
              className="pointer-events-none absolute inset-0 -z-10 origin-bottom-right rounded-full border border-blue-400 md:origin-right"
              variants={{
                open: {
                  transform: `scale(${1 + i * 0.1})`,
                  opacity: 1,
                },
                closed: {
                  transform: `scale(1)`,
                  opacity: 0,
                },
              }}
            />
          ))}
      </div>

      <div
        className={classNames(
          "absolute left-1/2 top-28 w-44 -translate-x-1/2 md:top-0 md:w-52",
          answer === "neither" ? "z-20" : "z-0"
        )}
      >
        {answer && answer === "neither" && (
          <div
            className={
              "absolute -inset-8 animate-pulse rounded-full bg-white/50"
            }
          />
        )}

        <Shape
          disabled={answer && answer !== "neither"}
          type="content"
          theme={"white"}
          shape={"circle"}
          onClick={handleClick("neither")}
          active={hasBeenClicked === "neither"}
        >
          <div className={"font-brik text-xl leading-none"}>
            No
            <br />
            Difference
          </div>
        </Shape>

        {!answer &&
          times(8, (i) => (
            <motion.div
              key={i}
              className="pointer-events-none absolute inset-0 rounded-full border border-white"
              variants={{
                open: {
                  transform: `scale(${1 + i * 0.15})`,
                  opacity: 1,
                },
                closed: {
                  transform: `scale(1)`,
                  opacity: 0,
                },
              }}
            />
          ))}
      </div>

      <div
        className={classNames(
          "relative z-10 w-44 md:w-52",
          answer === "b" ? "z-20" : "z-0"
        )}
      >
        {answer && answer === "b" && (
          <div
            className={
              "absolute -inset-8 animate-pulse rounded-full bg-red-500/50"
            }
          />
        )}

        <Shape
          disabled={answer && answer !== "b"}
          type="content"
          theme={"red"}
          shape={"circle"}
          onClick={handleClick("b")}
          active={hasBeenClicked === "b"}
        >
          <div className={"relative mb-4"}>
            <AnimatePresence>
              {playbackState === "b" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <SpeakerWaveIcon
                    className={
                      "absolute -top-5 left-1/2 h-5 w-5 -translate-x-1/2 animate-pulse"
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className={"font-brik text-6xl leading-none md:text-8xl"}>
              B
            </div>
            <div className={"font-brik text-xs leading-none"}>
              {answer && answer === "b" && answerText}
              {!answer && questionText}
            </div>
          </div>
        </Shape>

        {!answer &&
          times(12, (i) => (
            <motion.div
              key={i}
              className="pointer-events-none absolute inset-0 -z-10 origin-bottom-left rounded-full border border-red-500 md:origin-left"
              variants={{
                open: {
                  transform: `scale(${1 + i * 0.1})`,
                  opacity: 1,
                },
                closed: {
                  transform: `scale(1)`,
                  opacity: 0,
                },
              }}
            />
          ))}
      </div>
    </motion.div>
  );
}
